import React, { useState, useEffect } from 'react';
import { getAllMembers, searchMembers } from '../services/firestoreService';
import {useAuth} from "../context/AuthContext";

const Members = () => {
    const [members, setMembers] = useState([]);
    const [filteredMembers, setFilteredMembers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { user } = useAuth();

    useEffect(() => {
        loadMembers();
    }, []);

    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredMembers(members);
        } else {
            filterMembers(searchTerm);
        }
    }, [searchTerm, members]);

    const loadMembers = async () => {
        try {
            setLoading(true);
            setError('');
            const membersData = await getAllMembers();

            // Filter out current user from the members list
            const otherMembers = membersData.filter(member => member.id !== user.uid);
            setMembers(otherMembers);
            setFilteredMembers(otherMembers);
        } catch (error) {
            console.error('Error loading members:', error);
            setError('Failed to load members. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const filterMembers = async (term) => {
        try {
            if (term.trim() === '') {
                setFilteredMembers(members);
                return;
            }

            const searchResults = await searchMembers(term);
            // Filter out current user from search results
            const filteredResults = searchResults.filter(member => member.id !== user.uid);
            setFilteredMembers(filteredResults);
        } catch (error) {
            console.error('Error searching members:', error);
            // Fallback to client-side filtering if search fails
            const filtered = members.filter(member =>
                member.name?.toLowerCase().includes(term.toLowerCase()) ||
                member.email?.toLowerCase().includes(term.toLowerCase()) ||
                member.city?.toLowerCase().includes(term.toLowerCase()) ||
                member.state?.toLowerCase().includes(term.toLowerCase())
            );
            setFilteredMembers(filtered);
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const getInitials = (name) => {
        if (!name) return '?';
        return name
            .split(' ')
            .map(part => part.charAt(0))
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return 'Invalid Date';
        }
    };

    const getMemberStatus = (member) => {
        if (member.status === 'pending_verification') {
            return { label: 'Pending Verification', color: 'yellow' };
        }
        if (member.status === 'suspended') {
            return { label: 'Suspended', color: 'red' };
        }
        return { label: 'Active', color: 'green' };
    };

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow-sm">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Community Members</h3>
                </div>
                <div className="p-6">
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-lg shadow-sm">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Community Members</h3>
                </div>
                <div className="p-6">
                    <div className="text-center py-8">
                        <div className="text-red-500 text-4xl mb-4">⚠️</div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Members</h3>
                        <p className="text-gray-600 mb-4">{error}</p>
                        <button
                            onClick={loadMembers}
                            className="bg-primary-500 text-white px-6 py-2 rounded-lg hover:bg-primary-600 transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Community Members</h3>
                        <p className="text-sm text-gray-600 mt-1">
                            {filteredMembers.length} {filteredMembers.length === 1 ? 'member' : 'members'} found
                        </p>
                    </div>
                    <div className="relative w-80">
                        <input
                            type="text"
                            placeholder="Search members by name, email, or location..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>

            {/* Members List */}
            <div className="p-6">
                {filteredMembers.length === 0 ? (
                    <div className="text-center py-12">
                        <div className="text-gray-400 text-6xl mb-4">👥</div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            {searchTerm ? 'No members found' : 'No members available'}
                        </h3>
                        <p className="text-gray-600">
                            {searchTerm
                                ? 'Try adjusting your search terms'
                                : 'There are no other members in the community yet.'
                            }
                        </p>
                    </div>
                ) : (
                    <div className="grid gap-6">
                        {filteredMembers.map((member) => {
                            const status = getMemberStatus(member);
                            return (
                                <div key={member.id} className="flex items-center justify-between p-6 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                                    <div className="flex items-center space-x-4 flex-1">
                                        {/* Avatar */}
                                        <div className="relative">
                                            <div className="w-16 h-16 bg-primary-500 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                                                {getInitials(member.name)}
                                            </div>
                                            <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${
                                                status.color === 'green' ? 'bg-green-500' :
                                                    status.color === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'
                                            }`}></div>
                                        </div>

                                        {/* Member Info */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center space-x-2 mb-1">
                                                <h4 className="font-semibold text-gray-900 truncate">
                                                    {member.name || 'Unnamed Member'}
                                                </h4>
                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                    status.color === 'green' ? 'bg-green-100 text-green-800' :
                                                        status.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                                                }`}>
                          {status.label}
                        </span>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                                                <div className="flex items-center space-x-1">
                                                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                    </svg>
                                                    <span className="truncate">{member.email}</span>
                                                </div>

                                                <div className="flex items-center space-x-1">
                                                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                    </svg>
                                                    <span>Joined {formatDate(member.membershipDate)}</span>
                                                </div>

                                                {(member.city || member.state) && (
                                                    <div className="flex items-center space-x-1">
                                                        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                        </svg>
                                                        <span className="truncate">
                              {[member.city, member.state].filter(Boolean).join(', ')}
                            </span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Additional Info */}
                                            <div className="flex items-center space-x-4 mt-3 text-xs text-gray-500">
                        <span className="flex items-center space-x-1">
                          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>{member.totalContributions || 0} contributions</span>
                        </span>

                                                {member.beneficiaries && member.beneficiaries.length > 0 && (
                                                    <span className="flex items-center space-x-1">
                            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            <span>{member.beneficiaries.length} beneficiaries</span>
                          </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex flex-col space-y-2 ml-4">
                                        <button className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium whitespace-nowrap">
                                            Send Support
                                        </button>
                                        <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium whitespace-nowrap">
                                            View Profile
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Load More Button (for future pagination) */}
                {filteredMembers.length > 0 && (
                    <div className="mt-6 text-center">
                        <button className="border border-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-50 transition-colors font-medium">
                            Load More Members
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Members;